import React, { useState } from "react";
import {
    Card,
    CardContent,
    CardActions,
    Checkbox,
    Button,
    Typography,
    Divider,
    Grid,
    Link,
    Box,
    Chip,
    Stack,
    Tooltip,
} from "@mui/material";
import { formatCurrency, getContractSizeBracket } from "../../utils/utilFunctions";
import { ReferencesTableType } from "../../types/dataTypes";
import { Handshake, Public, Euro, Layers, RocketLaunch, Verified, Apartment, ArrowForward, Print } from '@mui/icons-material';
import IconTextRow from "../Shared/IconTextRow";

type ReferenceCardProps = {
    item: ReferencesTableType;
    isSelected: boolean;
    onDetailsClick: (projectNo: string) => void;
    onSelectChange: (projectNo: string) => void;
    setDrawerDetailsOpen: Function;
};

// Splits a "; " or ", " separated field (e.g. Field of Service) into trimmed tags for chip
// display, matching the same split already used elsewhere for this field (ReferenceFilters).
function splitTags(value?: string): string[] {
    if (!value) return [];
    return Array.from(new Set(value.split(/;\s|,\s/).map((part) => part.trim()).filter(Boolean)));
}

function ReferenceCard({ item, isSelected, onDetailsClick, onSelectChange }: ReferenceCardProps) {
    const [isExpanded, setIsExpanded] = useState(false);

    const handleExpandClick = () => {
        setIsExpanded(!isExpanded);
    };

    const text = item.ProjectDescription_short;
    const displayText = isExpanded ? text : (text !== undefined && text !== null ? `${text.substring(0, 200)}...` : '');
    const lastThreeChars = displayText.slice(-6);
    const textWithoutLastThreeChars = displayText.slice(0, -6);

    const contractSize = getContractSizeBracket(item.ProjectValueEuro);

    return (
        <Card sx={{ transition: 'box-shadow 0.2s ease', '&:hover': { boxShadow: 6 } }}>
            <Box sx={{ height: 4, backgroundColor: 'primary.main' }} />
            <CardContent sx={{ paddingBottom: 1 }}>
                <Link
                    component="button"
                    onClick={() => onDetailsClick(item.ProjectNo)}
                    underline="none"
                >
                    <Typography variant="h6" color={'primary'} style={{ textAlign: 'left', fontWeight: 700 }}>
                        {item.ProjectNo} - {item.ProjectTitle}
                    </Typography>
                </Link>
                <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ marginTop: 0.5, marginBottom: 1 }}>
                    <Chip
                        size="small"
                        icon={<Apartment fontSize="small" />}
                        label={item.INTEE === true ? 'INT_EE' : 'INT_FRA'}
                        variant="outlined"
                    />
                    {contractSize && (
                        <Tooltip title={`Contract value: ${contractSize.rangeLabel}`}>
                            <Chip
                                size="small"
                                label={contractSize.label}
                                sx={{
                                    backgroundColor: contractSize.backgroundColor,
                                    color: contractSize.textColor,
                                    fontWeight: 600,
                                }}
                            />
                        </Tooltip>
                    )}
                </Stack>

                <Divider sx={{ marginBottom: 1 }} />

                <Grid container justifyContent="space-evenly" rowSpacing={0.25}>
                    <Grid item xs={12} sm={2}>
                        <IconTextRow title="Client" IconComponent={Handshake} displayText={item.Client_short} />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <IconTextRow title="Country" IconComponent={Public} displayText={item.Country} country={item.Country} />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <IconTextRow title="Project Value" IconComponent={Euro} displayText={formatCurrency(item.ProjectValueEuro)} />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <IconTextRow title="Start Date" IconComponent={RocketLaunch} displayText={item.StartDate !== undefined && item.StartDate !== null ? item.StartDate.toString() : '-'} />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <IconTextRow title="Completion Date" IconComponent={Verified} displayText={item.CompletionDate !== undefined && item.CompletionDate !== null ? item.CompletionDate.toString() : '-'} />
                    </Grid>
                    <Grid item xs={12}>
                        <IconTextRow title="Field of Service" IconComponent={Layers} chips={splitTags(item.FieldOfService)} />
                    </Grid>
                </Grid>

                <Grid container>
                    <Grid item xs={12} paddingTop={1.5}>
                        <Typography variant="body1">
                            {textWithoutLastThreeChars}
                            <span onClick={handleExpandClick} style={{ color: 'green', cursor: 'pointer', fontWeight: 'bold' }}>
                                {lastThreeChars}
                            </span>
                        </Typography>
                    </Grid>
                </Grid>

            </CardContent>
            <Divider style={{ color: 'lightgrey' }} />
            <CardActions>
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item xs={6} container alignItems="center" spacing={1}>
                        <Grid item>
                            <Checkbox
                                checked={isSelected}
                                onChange={() => onSelectChange(item.ProjectNo)}
                                icon={<Print color="disabled" />}
                                checkedIcon={<Print color="primary" />}
                            />
                        </Grid>
                        <Grid item>
                            <Typography variant="body1">Select to Print</Typography>
                        </Grid>
                    </Grid>
                    <Grid item xs={6} container justifyContent="flex-end">
                        <Button
                            startIcon={<ArrowForward />}
                            size="small"
                            variant="text"
                            color="primary"
                            onClick={() => onDetailsClick(item.ProjectNo)}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            View Details
                        </Button>
                    </Grid>
                </Grid>
            </CardActions>
        </Card>
    );
};

export default ReferenceCard;
